import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createRepeatedDataResSkeleton,
  handlePhaseStatus,
} from "../../Components/Responses/utils";
import { PhaseListForms, PhasesList } from "../../Components/Responses/types";
import { FormQuestion, QuestionValidation } from "../../types/FormData.types";

export type Responses = {
  id?: string;
  isCleared?: boolean;
  remarkValue?: any;
  textValue?: any;
  numberValue?: any;
  questionChoiceId?: any;
  questionChoice?: any;
  files?: any;
  questionId?: string;
  gridData?: any; // FE
  repeatedData?: any; // FE
  questionValidation?: QuestionValidation;
  notSaved?: boolean; // FE
  aggregateData?: any;
  medications?: any[];
};

export type QuestionSlice = FormQuestion & {
  responses: Responses[];
  responsesBackup: Responses[];
  isVisible: boolean;
};

export type ExternalResponsesDict = {
  [key: string]: {
    textValue: any;
    numberValue: any;
    numberArray: any[];
    type: string;
    calculationOperands: string[];
    choiceLabel?: string;
    gridValue?: any;
    aggregateData?: any;
  };
};

export type ConfirmationModalProps = {
  show: boolean;
  varnames: string[];
  questionId: string;
  remarkValue?: string;
  studyId?: string;
  surveySlug?: string;
  isClearResponseSelected?: boolean;
  details: any[];
};

export interface ResponseState {
  toggleLoader: boolean;
  togglePhase: boolean;
  currentCategory: string;

  isArchived: boolean;
  isLocked: boolean;
  eligibilityStatus: string;
  isExcluded: boolean;
  isWithdrawn: boolean;
  editable: boolean;
  canRandomize: boolean;
  viewRandomization: boolean;
  canAmendQueries: boolean;
  canSendSurveys: boolean;
  canEditSurveys: boolean;
  canEditVisits: boolean;
  canEditScreening: boolean;
  hasEmail: boolean;
  canLock: boolean;

  isLoading: boolean;
  fieldSubmitting: string;
  checkingPermissions: boolean;
  isParticipant: boolean;

  participantId: string;
  siteId: string;
  subjectId: string;
  email: string;
  lang: string;
  isDataCollectionOpen: boolean;
  dataCollectionEndsOn: string | null;
  dataCollectionEndReason: string | null;

  repeatedAttemptId: string;
  surveyAssignmentId: string;
  screeningAttemptId: string;
  screeningBtn: string;

  phaseList: PhasesList[];
  selectedForm: PhaseListForms | null;
  selectedPhase: string;
  questionList: QuestionSlice[];

  queries: any;
  queryModal: any;
  formEditable: boolean;
  isAdminAttempted: boolean;

  calcFields: string[];
  externalResponses: ExternalResponsesDict;
  summaryExternalResponses: ExternalResponsesDict;
  showChangeConfirmModal: ConfirmationModalProps;
}

export const initialResponseState: ResponseState = {
  toggleLoader: false,
  togglePhase: false,
  currentCategory: "",

  isArchived: false,
  isLocked: false,
  isExcluded: false,
  isWithdrawn: false,
  eligibilityStatus: "",
  editable: false,
  canRandomize: false,
  viewRandomization: false,
  canAmendQueries: false,
  canSendSurveys: false,
  canEditSurveys: false,
  canEditVisits: false,
  canEditScreening: false,
  hasEmail: true,
  canLock: false,

  isLoading: false,
  fieldSubmitting: "",
  checkingPermissions: true,
  isParticipant: false,

  participantId: "",
  siteId: "",
  subjectId: "",
  email: "",
  lang: "",
  isDataCollectionOpen: true,
  dataCollectionEndsOn: null,
  dataCollectionEndReason: null,

  repeatedAttemptId: "",
  surveyAssignmentId: "",
  screeningAttemptId: "",
  screeningBtn: "",

  phaseList: [],
  selectedForm: null,
  questionList: [],
  selectedPhase: "",

  queries: {},
  queryModal: {},
  formEditable: false,
  isAdminAttempted: false,

  calcFields: [],
  externalResponses: {},
  summaryExternalResponses: {},
  showChangeConfirmModal: {
    show: false,
    varnames: [],
    questionId: "",
    details: [],
  },
};

export const responseSlice = createSlice({
  name: "response",
  initialState: initialResponseState,
  reducers: {
    reset: () => initialResponseState,
    resetResponseState: (state) => {
      state.isArchived = false;
      state.isLocked = false;
      state.isExcluded = false;
      state.isWithdrawn = false;
      state.eligibilityStatus = "";
      state.editable = false;
      state.canAmendQueries = false;
      state.canRandomize = false;
      state.canSendSurveys = false;
      state.viewRandomization = false;
      state.canEditSurveys = false;
      state.canEditVisits = false;
      state.canEditScreening = false;
      state.hasEmail = false;
      state.canLock = false;
      state.currentCategory = "";

      state.isLoading = true;
      state.fieldSubmitting = "";
      state.checkingPermissions = true;
      state.isParticipant = false;

      state.subjectId = "";
      state.participantId = "";
      state.siteId = "";
      state.email = "";
      state.lang = "";
      state.isDataCollectionOpen = true;
      state.dataCollectionEndsOn = null;
      state.dataCollectionEndReason = null;

      state.phaseList = [];
      state.selectedPhase = "";
      state.selectedForm = null;
      state.questionList = [];

      state.repeatedAttemptId = "";
      state.surveyAssignmentId = "";
      state.screeningAttemptId = "";
      state.screeningBtn = "";

      state.queries = {};
      state.queryModal = {};
      state.formEditable = false;
      state.isAdminAttempted = false;

      state.externalResponses = {};
      state.summaryExternalResponses = {};
      state.calcFields = [];
      state.showChangeConfirmModal = {
        show: false,
        varnames: [],
        questionId: "",
        details: [],
      };
    },
    resetResponseFormState: (state) => {
      state.isLoading = true;
      state.fieldSubmitting = "";
      state.checkingPermissions = false;
      state.selectedForm = null;
      state.queries = {};
      state.formEditable = false;
      state.questionList = [];
      state.repeatedAttemptId = "";
      state.surveyAssignmentId = "";
      state.screeningAttemptId = "";
      state.screeningBtn = "";
      state.queryModal = {};
      state.phaseList = [];
      state.selectedPhase = "";
      state.calcFields = [];
      state.externalResponses = {};
      state.summaryExternalResponses = {};
      state.isAdminAttempted = false;
      state.showChangeConfirmModal = {
        show: false,
        varnames: [],
        questionId: "",
        details: [],
      };
    },
    // setResponseState: (state, action: PayloadAction<ResponseState>) => {
    //   state.editable = action.payload.editable;
    //   state.isLoading = action.payload.isLoading;
    //   state.fieldSubmitting = action.payload.fieldSubmitting;
    //   state.checkingPermissions = action.payload.checkingPermissions;
    //   state.participantId = action.payload.participantId;
    //   state.selectedForm = action.payload.selectedForm;
    //   state.queries = action.payload.queries;
    //   state.questionList = action.payload.questionList;
    //   state.repeatedAttemptId = action.payload.repeatedAttemptId;
    //   state.queryModal = action.payload.queryModal;
    //   state.phaseList = action.payload.phaseList;
    // },
    setResponseCategory: (state, action: PayloadAction<string>) => {
      state.currentCategory = action.payload;
    },
    setEligibilityStatus: (state, action: PayloadAction<string>) => {
      state.eligibilityStatus = action.payload;
    },
    setResponsePermission: (state, action: PayloadAction<boolean>) => {
      state.checkingPermissions = action.payload;
    },
    setResponseLoader: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setResponseSubmitting: (state, action: PayloadAction<string>) => {
      state.fieldSubmitting = action.payload;
    },
    setResponseIsEditable: (state, action: PayloadAction<boolean>) => {
      state.editable = action.payload;
    },
    setResponseFormEditable: (state, action: PayloadAction<boolean>) => {
      state.formEditable = action.payload;
    },
    setIsAdminAttempted: (state, action: PayloadAction<boolean>) => {
      state.isAdminAttempted = action.payload;
    },
    setShowChangeConfirmModal: (
      state,
      action: PayloadAction<ConfirmationModalProps>,
    ) => {
      state.showChangeConfirmModal = action.payload;
    },
    setEmailSubjectId: (
      state,
      action: PayloadAction<{ email: string; subjectId: string }>,
    ) => {
      state.email = action.payload.email;
      state.subjectId = action.payload.subjectId;
    },
    setDataCollectionStatus: (
      state,
      action: PayloadAction<{
        isDataCollectionOpen: boolean;
        dataCollectionEndsOn: string | null;
        dataCollectionEndReason: string | null;
      }>,
    ) => {
      state.isDataCollectionOpen = action.payload.isDataCollectionOpen;
      state.dataCollectionEndsOn = action.payload.dataCollectionEndsOn;
      state.dataCollectionEndReason = action.payload.dataCollectionEndReason;
    },
    setResPermissions: (
      state,
      action: PayloadAction<{
        canRandomize: boolean;
        viewRandomization: boolean;
        canAmendQueries: boolean;
        canSendSurveys: boolean;
        canEditSurveys: boolean;
        canEditVisits: boolean;
        canEditScreening: boolean;
        editable: boolean;
        isExcluded: boolean;
        isLocked: boolean;
        isArchived: boolean;
        eligibilityStatus: string;
        hasEmail: boolean;
        canLock: boolean;
        isWithdrawn: boolean;
      }>,
    ) => {
      state.isExcluded = action.payload.isExcluded;
      state.isArchived = action.payload.isArchived;
      state.isLocked = action.payload.isLocked;
      state.canRandomize = action.payload.canRandomize;
      state.viewRandomization = action.payload.viewRandomization;
      state.canAmendQueries = action.payload.canAmendQueries;
      state.canSendSurveys = action.payload.canSendSurveys;
      state.canEditSurveys = action.payload.canEditSurveys;
      state.canEditVisits = action.payload.canEditVisits;
      state.canEditScreening = action.payload.canEditScreening;
      state.editable = action.payload.editable;
      state.hasEmail = action.payload.hasEmail;
      state.canLock = action.payload.canLock;
      state.isWithdrawn = action.payload.isWithdrawn;
      state.eligibilityStatus = action.payload.eligibilityStatus;
    },
    setParticipantId: (state, action: PayloadAction<string>) => {
      state.participantId = action.payload;
    },
    setParticipantLang: (state, action: PayloadAction<string>) => {
      state.lang = action.payload;
    },
    setSiteId: (state, action: PayloadAction<string>) => {
      state.siteId = action.payload;
    },
    setSelectedForm: (
      state,
      action: PayloadAction<{ form: PhaseListForms }>,
    ) => {
      state.selectedForm = action.payload.form;
      const formId = action.payload.form?.id;
      if (formId) {
        state.phaseList.forEach((phase) => {
          phase.phaseForms.forEach((form) => {
            if (form.id === formId) {
              form.name = action.payload.form.name;
            }
          });
        });
      }
    },
    setRepeatedAttemptId: (
      state,
      action: PayloadAction<{ repeatedAttemptId: string }>,
    ) => {
      state.repeatedAttemptId = action.payload.repeatedAttemptId;
    },
    setSurveyAttemptId: (
      state,
      action: PayloadAction<{ surveyAssignmentId: string }>,
    ) => {
      state.surveyAssignmentId = action.payload.surveyAssignmentId;
    },
    setScreeningAttemptId: (
      state,
      action: PayloadAction<{ screeningAttemptId: string }>,
    ) => {
      state.screeningAttemptId = action.payload.screeningAttemptId;
    },
    setScreeningBtn: (state, action: PayloadAction<string>) => {
      state.screeningBtn = action.payload;
    },
    setQuestionsList: (
      state,
      action: PayloadAction<{ list: QuestionSlice[] }>,
    ) => {
      state.questionList = action.payload.list;
    },
    revertQuestionChange: (state, action: PayloadAction<{ id: string }>) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          q.responses = q.responsesBackup;
        }
      });
    },
    handleSummaryUpdate: (
      state,
      action: PayloadAction<{
        label: string;
        id: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          q.label = action.payload.label;
        }
      });
    },
    handleFieldUpdate: (
      state,
      action: PayloadAction<{
        id: string;
        key: string;
        value: string;
        convertToInt?: boolean;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          let val = action.payload.convertToInt
            ? parseFloat(action.payload.value)
            : action.payload.value;
          if (action.payload.convertToInt && isNaN(val as number)) {
            val = "";
          }
          let obj: any = {
            notSaved: true,
            [action.payload.key]: val,
          };

          if (q.responses?.[0]?.remarkValue)
            obj.remarkValue = q.responses[0].remarkValue;

          q.responses[0] = obj;
        }
      });
    },
    handleRadioUpdate: (
      state,
      action: PayloadAction<{
        id: string;
        choiceId: string;
        textValue?: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          let obj: any = {
            notSaved: true,
            questionChoiceId: action.payload.choiceId,
          };

          if (q.responses?.[0]?.remarkValue)
            obj.remarkValue = q.responses[0].remarkValue;

          if (action.payload.textValue) {
            obj.textValue = action.payload.textValue;
          }

          q.responses[0] = obj;
        }
      });
    },
    handleCheckboxUpdate: (
      state,
      action: PayloadAction<{
        id: string;
        choiceId: string;
        textValue?: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          const selectedIds = q.responses
            .map((response: any) => response.questionChoiceId)
            .filter(Boolean);
          const clickedChoice = q.choices.find(
            (choice: any) => choice.id === action.payload.choiceId,
          );
          const isClickedSelected = selectedIds.includes(action.payload.choiceId);
          const maxSelections = q.properties?.maxSelections;
          const hasExclusiveSelected = q.responses.some((response: any) => {
            const selectedChoice = q.choices.find(
              (choice: any) => choice.id === response.questionChoiceId,
            );
            return !!selectedChoice?.isExclusive;
          });

          // Remove the choiceId from responses if it already exists
          const found = q.responses.find(
            (response: any) =>
              response.questionChoiceId === action.payload.choiceId,
          );

          if (found) {
            if (action.payload.textValue) {
              // Update the textValue
              found.textValue = action.payload.textValue;
            } else {
              // Remove the response
              q.responses = q.responses.filter(
                (response: any) =>
                  response.questionChoiceId !== action.payload.choiceId,
              );
            }
          } else {
            if (!clickedChoice) {
              return;
            }

            // If an exclusive choice is selected, only allow toggling that choice off.
            if (hasExclusiveSelected && !clickedChoice.isExclusive) {
              return;
            }

            // Selecting exclusive choice clears all existing non-exclusive responses.
            if (clickedChoice.isExclusive && !isClickedSelected) {
              q.responses = [];
            }

            // Selecting a non-exclusive choice clears existing exclusive selection.
            if (!clickedChoice.isExclusive && !isClickedSelected) {
              q.responses = q.responses.filter((response: any) => {
                const selectedChoice = q.choices.find(
                  (choice: any) => choice.id === response.questionChoiceId,
                );
                return !selectedChoice?.isExclusive;
              });
            }

            // Enforce max selections on non-exclusive choices.
            // Only count rows that map to a real choice so the empty
            // starter `{ notSaved: true }` placeholder is not treated as a pick.
            if (
              !clickedChoice.isExclusive &&
              !isClickedSelected &&
              typeof maxSelections === "number"
            ) {
              const nonExclusiveCount = q.responses.filter((response: any) => {
                const selectedChoice = q.choices.find(
                  (choice: any) => choice.id === response.questionChoiceId,
                );
                return !!selectedChoice && !selectedChoice.isExclusive;
              }).length;
              if (nonExclusiveCount >= maxSelections) {
                return;
              }
            }

            // Add the new response
            let obj: any = {
              notSaved: true,
              questionChoiceId: action.payload.choiceId,
            };

            if (action.payload.textValue) {
              obj.textValue = action.payload.textValue;
            }

            if (q.responses?.[0]?.remarkValue)
              obj.remarkValue = q.responses[0].remarkValue;

            q.responses = q.responses.filter(
              (response: any) => !!response.questionChoiceId,
            );
            q.responses.push(obj);
          }
        }
      });
    },
    handleCheckboxIsOtherUpdate: (
      state,
      action: PayloadAction<{
        id: string;

        choiceId: string;
        textValue: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          if (q.responses.length > 0) {
            let found = false;
            q.responses.forEach((response) => {
              if (response.questionChoiceId === action.payload.choiceId) {
                //If Already Exists
                found = true;
                response.textValue = action.payload.textValue;
              }
            });
            if (!found) {
              //If Not Already Exists
              let obj: any = {
                questionChoiceId: action.payload.choiceId,
                textValue: action.payload.textValue,
              };

              q.responses.push(obj);
            }
          } else {
            let obj: any = {
              questionChoiceId: action.payload.choiceId,
              textValue: action.payload.textValue,
            };

            q.responses.push(obj);
          }
        }
      });
    },
    handleGridUpdate: (
      state,
      action: PayloadAction<{
        id: string;
        convertToInt: boolean;
        rowIndex: number;
        colIndex: number;
        key: string;
        value: string;
      }>,
    ) => {
      let val = action.payload.convertToInt
        ? parseFloat(action.payload.value)
        : action.payload.value;
      if (action.payload.convertToInt && isNaN(val as number)) {
        val = "";
      }
      const obj = {
        [action.payload.key]: val,
      };

      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          if (q.responses?.[0]?.remarkValue)
            obj.remarkValue = q.responses[0].remarkValue;

          if (q.responses?.[0]?.gridData) {
            q.responses[0].gridData[action.payload.rowIndex][
              action.payload.colIndex
            ] = obj;
            q.responses[0].notSaved = true;
          }
        }
      });
    },
    handleGridAggregation: (
      state,
      action: PayloadAction<{
        id: string;
        rowIndex: number;
        colIndex: number;
        rowAggregation?: string;
        columnAggregation?: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          if (q.responses?.[0]?.gridData) {
            if (action.payload.columnAggregation) {
              let columnResult = 0;
              q.responses[0].gridData.forEach((row: any) => {
                if (
                  typeof row?.[action.payload.colIndex]?.numberValue ===
                  "number"
                ) {
                  columnResult += row[action.payload.colIndex].numberValue ?? 0;
                }
              });
              if (
                q?.responses?.[0]?.aggregateData?.column?.[
                  action.payload.colIndex
                ]
              ) {
                columnResult = Math.round(columnResult * 100) / 100;
                q.responses[0].aggregateData.column[action.payload.colIndex] = {
                  numberValue: columnResult,
                };
              }
            }

            if (action.payload.rowAggregation) {
              let rowResult = 0;
              q.responses[0].gridData[action.payload.rowIndex].forEach(
                (col: any) => {
                  if (typeof col.numberValue === "number") {
                    rowResult += +col.numberValue || 0;
                  }
                },
              );
              if (
                q?.responses?.[0]?.aggregateData?.row?.[action.payload.rowIndex]
              ) {
                rowResult = Math.round(rowResult * 100) / 100;
                q.responses[0].aggregateData.row[action.payload.rowIndex] = {
                  numberValue: rowResult,
                };
              }
            }
          }
        }
      });
    },
    handleRepeatedUpdate: (
      state,
      action: PayloadAction<{
        id: string;
        convertToInt: boolean;
        rowIndex: number;
        colIndex: number;
        key: string;
        value: string;
      }>,
    ) => {
      let val = action.payload.convertToInt
        ? parseFloat(action.payload.value)
        : action.payload.value;
      if (action.payload.convertToInt && isNaN(val as number)) {
        val = "";
      }
      const obj = {
        [action.payload.key]: val,
      };

      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          if (q.responses?.[0]?.remarkValue)
            obj.remarkValue = q.responses[0].remarkValue;

          if (q.responses?.[0]?.repeatedData) {
            q.responses[0].repeatedData[action.payload.rowIndex][
              action.payload.colIndex
            ] = obj;
            q.responses[0].notSaved = true;
          }
        }
      });
    },
    handleAddRepeatedData: (
      state,
      action: PayloadAction<{
        id: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          const obj = createRepeatedDataResSkeleton(
            q.properties.repeatedConfig,
          );
          if (q.responses?.[0]?.repeatedData) {
            const newConfig = obj[0].repeatedData[0];
            q.responses[0].repeatedData.push(newConfig);
            if (q?.responses?.[0]?.aggregateData?.row.length > 0) {
              q?.responses?.[0]?.aggregateData?.row.push({});
            }
            q.responses[0].notSaved = true;
          }
        }
      });
    },
    handleRemoveRepeatedData: (
      state,
      action: PayloadAction<{
        id: string;
        index: number;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id && q.responses?.[0]?.repeatedData) {
          q.responses[0].repeatedData.splice(action.payload.index, 1);
          if (q?.responses?.[0]?.aggregateData?.row.length > 0) {
            q?.responses?.[0]?.aggregateData?.row.splice(
              action.payload.index,
              1,
            );
          }
          q.responses[0].notSaved = true;
        }
      });
    },
    handleRepeatedDataAggregation: (
      state,
      action: PayloadAction<{
        id: string;
        rowIndex: number;
        colIndex: number;
        rowAggregation?: string;
        columnAggregation?: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          if (q.responses?.[0]?.repeatedData) {
            if (action.payload.columnAggregation) {
              let columnResult = 0;
              q.responses[0].repeatedData.forEach((row: any) => {
                if (
                  typeof row?.[action.payload.colIndex]?.numberValue ===
                  "number"
                ) {
                  columnResult += row[action.payload.colIndex].numberValue ?? 0;
                }
              });
              if (
                q?.responses?.[0]?.aggregateData?.column?.[
                  action.payload.colIndex
                ]
              ) {
                columnResult = Math.round(columnResult * 100) / 100;
                q.responses[0].aggregateData.column[action.payload.colIndex] = {
                  numberValue: columnResult,
                };
              }
            }

            if (action.payload.rowAggregation) {
              let rowResult = 0;
              q.responses[0].repeatedData[action.payload.rowIndex].forEach(
                (col: any) => {
                  if (typeof col.numberValue === "number") {
                    rowResult += +col.numberValue || 0;
                  }
                },
              );
              if (
                q?.responses?.[0]?.aggregateData?.row?.[action.payload.rowIndex]
              ) {
                rowResult = Math.round(rowResult * 100) / 100;
                q.responses[0].aggregateData.row[action.payload.rowIndex] = {
                  numberValue: rowResult,
                };
              }
            }
          }
        }
      });
    },
    handleRepeatedColumnAggregation: (
      state,
      action: PayloadAction<{
        id: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id && q.responses?.[0]?.repeatedData) {
          const columns = q.properties.repeatedConfig.columns;
          const aggregateArray = new Array(columns.length).fill(0);
          q.responses[0].repeatedData.forEach((row: any) => {
            for (let i = 0; i < columns.length; i++) {
              const column = columns[i];
              if (
                column.aggregateType &&
                typeof row[i].numberValue === "number"
              ) {
                aggregateArray[i] += row[i].numberValue;
              }
            }
          });
          for (let i = 0; i < columns.length; i++) {
            const column = columns[i];
            if (column.aggregateType) {
              aggregateArray[i] = Math.round(aggregateArray[i] * 100) / 100;
              q.responses[0].aggregateData.column[i] = {
                numberValue: aggregateArray[i],
              };
            }
          }
        }
      });
    },
    handleMedicationUpdate: (
      state,
      action: PayloadAction<{ index: number; medication: any; id: string }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id && q.responses[0].medications) {
          let medications = q.responses[0].medications;
          if (action.payload.index !== undefined) {
            medications[action.payload.index] = action.payload.medication;
          } else {
            medications.push(action.payload.medication);
          }
        }
      });
    },
    handleMedicationDelete: (
      state,
      action: PayloadAction<{ index: number; id: string }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id && q.responses[0].medications) {
          let medications = q.responses[0].medications;
          medications.splice(action.payload.index, 1);
        }
      });
    },
    setQueries: (
      state,
      action: PayloadAction<{
        queries: any;
      }>,
    ) => {
      state.queries = action.payload.queries;
    },
    setQueryModal: (
      state,
      action: PayloadAction<{
        queryModal: any;
      }>,
    ) => {
      state.queryModal = action.payload.queryModal;
    },
    setPhaseList: (
      state,
      action: PayloadAction<{
        list: PhasesList[];
      }>,
    ) => {
      state.phaseList = action.payload.list;
    },
    setSelectedPhase: (
      state,
      action: PayloadAction<{
        phase: string;
        toggle?: boolean;
      }>,
    ) => {
      if (action.payload.toggle) {
        state.selectedPhase =
          state.selectedPhase === action.payload.phase
            ? ""
            : action.payload.phase;
      } else {
        state.selectedPhase = action.payload.phase;
      }
    },
    handleFormStatus: (state, action: PayloadAction<string>) => {
      const status = action.payload;
      const formId = state.selectedForm?.id;
      if (!formId) {
        return;
      }

      const resolvePhaseStatus = handlePhaseStatus;

      let found = false;
      state.phaseList.forEach((phase) => {
        phase.phaseForms.forEach((form) => {
          if (form.id === formId) {
            form.status = status;
            found = true;
          }
        });
        phase.phaseStatus = resolvePhaseStatus(phase.phaseForms);
      });

      if (state.selectedForm) {
        state.selectedForm = { ...state.selectedForm, status };
      }

      if (!found && state.selectedForm) {
        const phase =
          state.phaseList.find((p) => p.id === state.selectedForm?.phaseId) ??
          state.phaseList[0];
        if (phase) {
          phase.phaseForms = [{ ...state.selectedForm, status }];
          phase.phaseStatus = resolvePhaseStatus(phase.phaseForms);
        }
      }
    },
    handleReadyToSubmit: (state, action: PayloadAction<boolean>) => {
      const readyToSubmit = action.payload;
      const formId = state.selectedForm?.id;
      if (!formId) {
        return;
      }
      state.phaseList.forEach((phase) => {
        phase.phaseForms.forEach((form) => {
          if (form.id === formId) {
            form.readyToSubmit = readyToSubmit;
          }
        });
      });
      if (state.selectedForm) {
        state.selectedForm = { ...state.selectedForm, readyToSubmit };
      }
    },
    handleFormSubmitted: (
      state,
      action: PayloadAction<{
        isSubmitted: boolean;
        submittedAt?: string;
      }>,
    ) => {
      const { isSubmitted, submittedAt } = action.payload;
      const formId = state.selectedForm?.id;
      if (!formId) {
        return;
      }

      const resolvePhaseStatus = handlePhaseStatus;

      let found = false;
      state.phaseList.forEach((phase) => {
        phase.phaseForms.forEach((form) => {
          if (form.id === formId) {
            form.isSubmitted = isSubmitted;
            if (isSubmitted) {
              form.status = "completed";
              form.readyToSubmit = false;
            }
            if (submittedAt !== undefined) {
              form.submittedAt = submittedAt;
            }
            found = true;
          }
        });
        phase.phaseStatus = resolvePhaseStatus(phase.phaseForms);
      });

      if (state.selectedForm) {
        state.selectedForm = {
          ...state.selectedForm,
          isSubmitted,
          ...(isSubmitted
            ? { status: "completed", readyToSubmit: false }
            : {}),
          ...(submittedAt !== undefined ? { submittedAt } : {}),
        };
      }

      if (!found && state.selectedForm) {
        const phase =
          state.phaseList.find((p) => p.id === state.selectedForm?.phaseId) ??
          state.phaseList[0];
        if (phase) {
          phase.phaseForms = [{ ...state.selectedForm, isSubmitted }];
          phase.phaseStatus = resolvePhaseStatus(phase.phaseForms);
        }
      }
    },
    setParticipantScreeningPhaseForm: (
      state,
      action: PayloadAction<{ form: PhaseListForms }>,
    ) => {
      const form = action.payload.form;
      state.selectedForm = form;
      const phase =
        state.phaseList.find((p) => p.id === form.phaseId) ?? state.phaseList[0];
      if (phase) {
        phase.phaseForms = [form];
        phase.phaseStatus = handlePhaseStatus(phase.phaseForms);
      }
    },
    handleAddFile: (
      state,
      action: PayloadAction<{
        fileObj: { url: string; name: string };
        id: string;
      }>,
    ) => {
      const fileObj = action.payload.fileObj;

      let obj: any = {
        notSaved: true,
      };

      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          if (!q.responses?.[0]?.files) {
            if (q.responses?.[0]?.remarkValue)
              obj.remarkValue = q.responses[0].remarkValue;

            obj.files = [fileObj];
            q.responses[0] = obj;
          } else {
            q.responses[0].notSaved = true;
            q.responses[0].files.push(fileObj);
          }
        }
      });
    },
    handleRemoveFile: (
      state,
      action: PayloadAction<{
        index: number;
        id: string;
      }>,
    ) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload.id) {
          q.responses?.[0]?.files?.splice?.(action.payload.index, 1);
        }
      });
    },
    setCalcFields: (
      state,
      action: PayloadAction<{
        f: string[];
      }>,
    ) => {
      const arr = [...state.calcFields, ...action.payload.f];
      state.calcFields = [...new Set(arr)];
    },
    popCalcFields: (state) => {
      state.calcFields = state.calcFields.slice(0, -1);
    },
    setExternalResponses: (
      state,
      action: PayloadAction<ExternalResponsesDict>,
    ) => {
      state.externalResponses = action.payload;
    },
    setSummaryExternalResponses: (
      state,
      action: PayloadAction<ExternalResponsesDict>,
    ) => {
      state.summaryExternalResponses = action.payload;
    },

    setNotSaved: (state, action: PayloadAction<string>) => {
      state.questionList.forEach((q) => {
        if (q.id === action.payload) {
          q.responses[0].notSaved = false;
          if (q.responsesBackup?.[0]?.questionValidation) {
            q.responses[0].questionValidation =
              q.responsesBackup[0].questionValidation;
          }
        }
      });
    },
    toggleResponseState: (state) => {
      state.toggleLoader = !state.toggleLoader;
    },
    togglePhaseLoader: (state) => {
      state.togglePhase = !state.togglePhase;
    },
    setIsParticipant: (state, action: PayloadAction<boolean>) => {
      state.isParticipant = action.payload;
    },
  },
});

export const {
  resetResponseState,
  resetResponseFormState,
  setResponseCategory,
  // setResponseState,
  setResponseLoader,
  setEligibilityStatus,
  setResponseSubmitting,
  setSelectedForm,
  setPhaseList,
  setSelectedPhase,
  handleFormStatus,
  handleReadyToSubmit,
  handleFormSubmitted,
  setParticipantScreeningPhaseForm,
  setQuestionsList,
  handleFieldUpdate,
  handleRadioUpdate,
  handleCheckboxUpdate,
  handleCheckboxIsOtherUpdate,
  setRepeatedAttemptId,
  setSurveyAttemptId,
  setScreeningAttemptId,
  setScreeningBtn,
  handleGridUpdate,
  handleAddRepeatedData,
  handleRemoveRepeatedData,
  handleRepeatedUpdate,
  setQueries,
  setEmailSubjectId,
  setDataCollectionStatus,
  setQueryModal,
  handleAddFile,
  handleRemoveFile,
  setParticipantId,
  setSiteId,
  setResponseIsEditable,
  setResPermissions,
  setResponsePermission,
  setCalcFields,
  popCalcFields,
  setExternalResponses,
  setSummaryExternalResponses,
  revertQuestionChange,
  setNotSaved,
  setShowChangeConfirmModal,
  reset,
  toggleResponseState,
  togglePhaseLoader,
  setResponseFormEditable,
  setIsAdminAttempted,
  handleSummaryUpdate,
  handleGridAggregation,
  handleRepeatedDataAggregation,
  handleRepeatedColumnAggregation,
  setParticipantLang,
  setIsParticipant,
  handleMedicationUpdate,
  handleMedicationDelete,
} = responseSlice.actions;

export default responseSlice.reducer;
